import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { ApproveRequiredTypesEnum } from 'APIPackage/KnmModule/enums/ApproveRequiredTypesEnum';
import { IKnmString } from 'APIPackage/KnmModule/interfaces/IKnmString';

import { IKnmErknmForm } from '../interfaces/IKnmErknmForm';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';

export interface IFieldForApprovingContext {
  settings: IAdminSetting[] | undefined;
  values: IKnmErknmForm;
}

interface IFieldForApproving {
  name: keyof IKnmErknmForm;
  requiredFieldName?: string;
  additionalCondition?: (value: unknown, context: IFieldForApprovingContext) => boolean;
  nested?: {
    name: string;
    pairedName?: string;
    typeCode?: KnmTypeCodesEnum;
  }[];
  paired?: {
    name: keyof IKnmErknmForm;
    getAdditionalCondition?: (value: unknown) => boolean;
  }[];
}

function hasOwnProperty<T>(obj: unknown | undefined, prop: string): obj is T {
  if (obj) {
    return Object.prototype.hasOwnProperty.call(obj, prop);
  }

  return false;
}

const checklistCondition = (value: unknown, context: IFieldForApprovingContext) => {
  const { created, typeErknm, approveRequired, kindKnm } = context.values;

  return (
    isLaterThanAdminSetting(created, context.settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_55_STARTS_FROM) &&
    typeErknm?.code === KnmTypeCodesEnum.VP &&
    approveRequired?.value === ApproveRequiredTypesEnum.REQUIRED &&
    (kindKnm?.code === KnmKindCodesEnum.RO ||
      kindKnm?.code === KnmKindCodesEnum.VP ||
      kindKnm?.code === KnmKindCodesEnum.IV)
  );
};

export const fieldsForApproving: IFieldForApproving[] = [
  {
    name: 'knoOrganizationErknm',
  },
  {
    name: 'places',
    paired: [
      {
        name: 'kindKnm',
        getAdditionalCondition: (value: unknown) => {
          if (hasOwnProperty<{ code: string }>(value, 'code')) {
            return value.code !== KnmKindCodesEnum.DP;
          }

          return false;
        },
      },
      {
        name: 'places',
        getAdditionalCondition: (value: IKnmString[] | undefined) => {
          if (!value || value.length === 0) {
            return true;
          }

          return !!value?.find(place => !place.notInFIAS && !place.value);
        },
      },
    ],
  },
  { name: 'kindControl' },
  { name: 'kindKnm' },
  { name: 'typeErknm' },
  {
    name: 'planId',
    paired: [
      {
        name: 'typeErknm',
        getAdditionalCondition: (value: unknown) => {
          if (hasOwnProperty<{ code?: string }>(value, 'code')) {
            return value?.code === KnmTypeCodesEnum.PP;
          }

          return false;
        },
      },
    ],
  },
  { name: 'prosecutorOrganizationErknm' },
  {
    name: 'approveDocs',
    requiredFieldName: 'approveDoc',
    paired: [{ name: 'requirementsErknm' }, { name: 'links' }, { name: 'requirementsNpaErknm' }],
  },
  {
    name: 'requirementsNpaErknm',
    requiredFieldName: 'npa',
    paired: [{ name: 'approveDocs' }, { name: 'links' }, { name: 'requirementsErknm' }],
  },
  {
    name: 'requirementsErknm',
    requiredFieldName: 'requirement',
    paired: [{ name: 'approveDocs' }, { name: 'links' }, { name: 'requirementsNpaErknm' }],
  },
  {
    name: 'links',
    requiredFieldName: 'id',
    paired: [{ name: 'approveDocs' }, { name: 'requirementsErknm' }, { name: 'requirementsNpaErknm' }],
  },
  {
    name: 'dateTimeDecision',
    paired: [
      {
        name: 'typeErknm',
        getAdditionalCondition: (value: unknown) => {
          if (hasOwnProperty<{ code?: string }>(value, 'code')) {
            return value?.code !== KnmTypeCodesEnum.PP;
          }

          return false;
        },
      },
    ],
  },
  {
    name: 'numberDecision',
    paired: [
      {
        name: 'typeErknm',
        getAdditionalCondition: (value: unknown) => {
          if (hasOwnProperty<{ code?: string }>(value, 'code')) {
            return value?.code !== KnmTypeCodesEnum.PP;
          }

          return false;
        },
      },
    ],
  },
  {
    name: 'numberDecision',
    paired: [
      {
        name: 'typeErknm',
        getAdditionalCondition: (value: unknown) => {
          if (hasOwnProperty<{ code?: string }>(value, 'code')) {
            return value?.code !== KnmTypeCodesEnum.PP;
          }

          return false;
        },
      },
    ],
  },
  {
    name: 'placeDecision',
    paired: [
      {
        name: 'typeErknm',
        getAdditionalCondition: (value: unknown) => {
          if (hasOwnProperty<{ code?: string }>(value, 'code')) {
            return value?.code !== KnmTypeCodesEnum.PP;
          }

          return false;
        },
      },
    ],
  },
  {
    name: 'fioSigner',
    paired: [
      {
        name: 'typeErknm',
        getAdditionalCondition: (value: unknown) => {
          if (hasOwnProperty<{ code?: string }>(value, 'code')) {
            return value?.code !== KnmTypeCodesEnum.PP;
          }

          return false;
        },
      },
    ],
  },
  {
    name: 'titleSigner',
    paired: [
      {
        name: 'typeErknm',
        getAdditionalCondition: (value: unknown) => {
          if (hasOwnProperty<{ code?: string }>(value, 'code')) {
            return value?.code !== KnmTypeCodesEnum.PP;
          }

          return false;
        },
      },
    ],
  },
  { name: 'startDate' },
  { name: 'durationDays', paired: [{ name: 'durationHours' }] },
  { name: 'durationHours', paired: [{ name: 'durationDays' }] },
  { name: 'approveRequired' },
  {
    name: 'objectsErknm',
    nested: [
      { name: 'address' },
      { name: 'objectType' },
      { name: 'objectKind' },
      { name: 'riskCategory', pairedName: 'dangerClass', typeCode: KnmTypeCodesEnum.PP },
      { name: 'dangerClass', pairedName: 'riskCategory', typeCode: KnmTypeCodesEnum.PP },
    ],
  },
  {
    name: 'objectsErknm',
    requiredFieldName: 'objectType',
  },
  { name: 'reasonsErknm', requiredFieldName: 'type' },
  { name: 'reasonsErknm', nested: [{ name: 'type' }] },
  {
    name: 'reasonDocuments',
    requiredFieldName: 'type',
    paired: [
      {
        name: 'approveRequired',
        getAdditionalCondition: (approveRequired: unknown) => {
          if (hasOwnProperty<{ value?: string }>(approveRequired, 'value')) {
            return approveRequired?.value !== ApproveRequiredTypesEnum.NON_REQUIRED;
          }

          return false;
        },
      },
    ],
  },
  {
    name: 'approvedOnPaperDocuments',
    requiredFieldName: 'isApprovedOnPaper',
    paired: [
      {
        name: 'isApprovedOnPaper',
        getAdditionalCondition: (value: unknown): boolean => {
          return value === true;
        },
      },
    ],
  },
  {
    name: 'eventsErknm',
    nested: [{ name: 'type' }, { name: 'interactionCategory' }, { name: 'startDate' }, { name: 'stopDate' }],
  },
  { name: 'eventsErknm', requiredFieldName: 'type' },
  { name: 'organizations', nested: [{ name: 'organizationName' }] },
  { name: 'interactionCategory' },
  { name: 'checklistsErknm', requiredFieldName: 'checkList', additionalCondition: checklistCondition },
];
